import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Search, 
  ChevronLeft, 
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Download,
  Eye,
  Edit,
  Trash2,
  UserPlus,
  Ban,
  CheckCircle
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getTierBadgeClass = (tier) => {
  switch (tier) {
    case "bronze": return "bg-amber-900/50 text-amber-300 border-amber-700";
    case "silver": return "bg-slate-600/50 text-slate-200 border-slate-500";
    case "gold": return "bg-yellow-900/50 text-yellow-300 border-yellow-700";
    case "platinum": return "bg-purple-900/50 text-purple-300 border-purple-700";
    default: return "bg-slate-700 text-slate-300 border-slate-600";
  }
};

const getStatusBadgeClass = (status) => {
  switch (status) {
    case "active": return "bg-green-900/50 text-green-300 border-green-700";
    case "suspended": return "bg-red-900/50 text-red-300 border-red-700";
    default: return "bg-slate-700 text-slate-300 border-slate-600";
  }
};

const AdminPartners = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [partners, setPartners] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0
  });
  
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [tierFilter, setTierFilter] = useState(searchParams.get("tier") || "");
  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "created_at");
  const [sortDir, setSortDir] = useState(searchParams.get("sortDir") || "desc");

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, partner: null });
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", partner_type: "influencer", tier: "bronze" });
  const [editForm, setEditForm] = useState({});

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString(),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      
      if (search) params.append("search", search);
      if (tierFilter) params.append("tier", tierFilter);
      
      const response = await axios.get(`${API}/admin/partners?${params}`, { withCredentials: true });
      setPartners(response.data.partners);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error("Failed to load partners");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, [pagination.page, sortBy, sortDir, tierFilter]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchPartners();
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExport = async () => {
    try {
      window.open(`${API}/admin/partners/export/csv`, "_blank");
      toast.success("Export started");
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const handleCreate = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("Please fill in all required fields");
      return;
    }
    try {
      await axios.post(`${API}/admin/partners/create`, createForm, { withCredentials: true });
      toast.success("Partner created successfully");
      setCreateDialog(false);
      setCreateForm({ name: "", email: "", password: "", partner_type: "influencer", tier: "bronze" });
      fetchPartners();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create partner");
    }
  };

  const handleEdit = (partner) => {
    setEditForm({
      name: partner.name,
      email: partner.email,
      tier: partner.tier,
      commission_rate: partner.commission_rate
    });
    setEditDialog({ open: true, partner });
  };

  const handleSaveEdit = async () => {
    try {
      await axios.put(`${API}/admin/partners/${editDialog.partner.partner_id}`, editForm, { withCredentials: true });
      toast.success("Partner updated successfully");
      setEditDialog({ open: false, partner: null });
      fetchPartners();
    } catch (err) {
      toast.error("Failed to update partner");
    }
  };

  const handleSuspend = async (partnerId) => {
    if (!window.confirm("Are you sure you want to suspend this partner?")) return;
    try {
      await axios.post(`${API}/admin/partners/${partnerId}/suspend`, {}, { withCredentials: true });
      toast.success("Partner suspended");
      fetchPartners();
    } catch (err) {
      toast.error("Failed to suspend partner");
    }
  };

  const handleActivate = async (partnerId) => {
    try {
      await axios.post(`${API}/admin/partners/${partnerId}/activate`, {}, { withCredentials: true });
      toast.success("Partner activated");
      fetchPartners();
    } catch (err) {
      toast.error("Failed to activate partner");
    }
  };

  const handleDelete = async (partnerId) => {
    if (!window.confirm("Are you sure you want to DELETE this partner? This will also delete all their referrals and cannot be undone.")) return;
    try {
      await axios.delete(`${API}/admin/partners/${partnerId}`, { withCredentials: true });
      toast.success("Partner deleted");
      fetchPartners();
    } catch (err) {
      toast.error("Failed to delete partner");
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 ml-1 text-slate-500" />;
    return sortDir === "asc" 
      ? <ArrowUp className="w-4 h-4 ml-1 text-purple-400" />
      : <ArrowDown className="w-4 h-4 ml-1 text-purple-400" />;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Partners</h1>
          <p className="text-slate-400 mt-1">Manage all partner accounts</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreateDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
            <UserPlus className="w-4 h-4 mr-2" />
            Add Partner
          </Button>
          <Button onClick={handleExport} variant="outline" className="border-slate-600 text-slate-300 hover:bg-slate-700">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  placeholder="Search by name, email, or code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                />
              </div>
            </form>
            
            <Select value={tierFilter || "all"} onValueChange={(v) => { setTierFilter(v === "all" ? "" : v); setPagination(prev => ({ ...prev, page: 1 })); }}>
              <SelectTrigger className="w-40 bg-slate-700 border-slate-600 text-slate-300">
                <SelectValue placeholder="All Tiers" />
              </SelectTrigger>
              <SelectContent className="bg-slate-700 border-slate-600">
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="bronze">Bronze</SelectItem>
                <SelectItem value="silver">Silver</SelectItem>
                <SelectItem value="gold">Gold</SelectItem>
                <SelectItem value="platinum">Platinum</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-16 bg-slate-700" />
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Partner</TableHead>
                    <TableHead className="text-slate-400">Status</TableHead>
                    <TableHead 
                      className="text-slate-400 cursor-pointer hover:text-white"
                      onClick={() => handleSort("tier")}
                    >
                      <div className="flex items-center">
                        Tier
                        <SortIcon field="tier" />
                      </div>
                    </TableHead>
                    <TableHead className="text-slate-400 text-right">Commission</TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-white"
                      onClick={() => handleSort("active_referrals")}
                    >
                      <div className="flex items-center justify-end">
                        Active
                        <SortIcon field="active_referrals" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="text-slate-400 text-right cursor-pointer hover:text-white"
                      onClick={() => handleSort("total_earnings")}
                    >
                      <div className="flex items-center justify-end">
                        Earnings
                        <SortIcon field="total_earnings" />
                      </div>
                    </TableHead>
                    <TableHead className="text-slate-400 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-12">
                        No partners found
                      </TableCell>
                    </TableRow>
                  ) : (
                    partners.map((partner) => (
                      <TableRow key={partner.partner_id} className="border-slate-700 hover:bg-slate-700/50">
                        <TableCell>
                          <div>
                            <p className="font-medium text-white">{partner.name}</p>
                            <p className="text-sm text-slate-400">{partner.email}</p>
                            <p className="text-xs text-slate-500">{partner.partner_code}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadgeClass(partner.status || "active")} capitalize`}>
                            {partner.status || "active"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getTierBadgeClass(partner.tier)} capitalize`}>
                            {partner.tier}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-slate-300">
                          {partner.commission_rate}%
                        </TableCell>
                        <TableCell className="text-right text-emerald-400 font-medium">
                          {partner.active_referrals}
                        </TableCell>
                        <TableCell className="text-right text-white font-medium">
                          ${partner.total_earnings?.toLocaleString() || 0}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-center gap-1">
                            <Link to={`/admin/partners/${partner.partner_id}`}>
                              <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300 hover:bg-blue-500/10">
                                <Eye className="w-4 h-4" />
                              </Button>
                            </Link>
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(partner)} className="text-yellow-400 hover:text-yellow-300 hover:bg-yellow-500/10">
                              <Edit className="w-4 h-4" />
                            </Button>
                            {partner.status === "suspended" ? (
                              <Button variant="ghost" size="sm" onClick={() => handleActivate(partner.partner_id)} className="text-green-400 hover:text-green-300 hover:bg-green-500/10">
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button variant="ghost" size="sm" onClick={() => handleSuspend(partner.partner_id)} className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10">
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(partner.partner_id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-700">
              <p className="text-sm text-slate-400">
                Showing {(pagination.page - 1) * pagination.pageSize + 1} to{" "}
                {Math.min(pagination.page * pagination.pageSize, pagination.totalCount)} of{" "}
                {pagination.totalCount} partners
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={!pagination.has_prev}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-400">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={!pagination.has_next}
                  className="border-slate-600 text-slate-300 hover:bg-slate-700"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Partner Dialog */}
      <Dialog open={createDialog} onOpenChange={setCreateDialog}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Add New Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Name *</Label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
                placeholder="Partner name"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email *</Label>
              <Input
                type="email"
                value={createForm.email}
                onChange={(e) => setCreateForm(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
                placeholder="partner@example.com"
              />
            </div>
            <div>
              <Label className="text-slate-300">Password *</Label>
              <Input
                type="password"
                value={createForm.password}
                onChange={(e) => setCreateForm(prev => ({ ...prev, password: e.target.value }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
                placeholder="Initial password"
              />
            </div>
            <div>
              <Label className="text-slate-300">Partner Type</Label>
              <Select value={createForm.partner_type} onValueChange={(v) => setCreateForm(prev => ({ ...prev, partner_type: v }))}>
                <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="influencer">Influencer</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                  <SelectItem value="affiliate">Affiliate</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Starting Tier</Label>
              <Select value={createForm.tier} onValueChange={(v) => setCreateForm(prev => ({ ...prev, tier: v }))}>
                <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="bronze">Bronze (30%)</SelectItem>
                  <SelectItem value="silver">Silver (35%)</SelectItem>
                  <SelectItem value="gold">Gold (40%)</SelectItem>
                  <SelectItem value="platinum">Platinum (45%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleCreate} className="bg-emerald-600 hover:bg-emerald-700">
              Create Partner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Partner Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, partner: editDialog.partner })}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Partner</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-300">Name</Label>
              <Input
                value={editForm.name || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Email</Label>
              <Input
                type="email"
                value={editForm.email || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300">Tier</Label>
              <Select value={editForm.tier} onValueChange={(v) => setEditForm(prev => ({ ...prev, tier: v }))}>
                <SelectTrigger className="mt-1 bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-700 border-slate-600">
                  <SelectItem value="bronze">Bronze</SelectItem>
                  <SelectItem value="silver">Silver</SelectItem>
                  <SelectItem value="gold">Gold</SelectItem>
                  <SelectItem value="platinum">Platinum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-slate-300">Commission Rate (%)</Label>
              <Input
                type="number"
                value={editForm.commission_rate || ""}
                onChange={(e) => setEditForm(prev => ({ ...prev, commission_rate: parseFloat(e.target.value) || 0 }))}
                className="mt-1 bg-slate-700 border-slate-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog({ open: false, partner: null })} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} className="bg-emerald-600 hover:bg-emerald-700">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPartners;
